import { useMemo, type FC } from 'react';

import type { MenuProps } from '@mui/material';
import type { Control, ValidationRule } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import InputDropdown from 'components/UI/molecules/InputDropdown/InputDropdown';
import type { Props as InputDropdownProps } from 'components/UI/molecules/InputDropdown/InputDropdown';
import type { DropdownOption } from 'constants/_types/DropdownOptions';
import validationMessages from 'translations/common/validation.mjs';

type Props = {
  label?: string;
  name: string;
  control: Control<any>;
  dropdownMinWidth?: number;
  options: DropdownOption[];
  hasFullHeight?: boolean;
  helperText?: string;
  required?: boolean;
  defaultValue?: string;
  placeholder?: string;
  rules?: ValidationRule<any>;
  inputProps?: Omit<InputDropdownProps, 'value'>;
  multiple?: boolean;
  size?: 'small' | 'medium';
  onValueUpdate?: (value: any) => void;
  menuProps?: MenuProps;
};

const FormInputDropdown: FC<Props> = ({
  control,
  label,
  hasFullHeight = false,
  dropdownMinWidth,
  helperText,
  name,
  defaultValue = '',
  inputProps = {},
  rules = {},
  required = false,
  options,
  placeholder,
  multiple,
  size = 'medium',
  onValueUpdate,
  menuProps,
}) => {
  const { t, i18n } = useTranslation();

  const rulesProp: { [key: string]: ValidationRule } = useMemo(
    () => ({ required: { value: required, message: t(validationMessages.required_field) }, ...rules }),
    [rules, required, i18n.language],
  );

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState }) => {
        // for multiple choices...
        const value: any | any[] = multiple && !Array.isArray(field.value) ? [field.value].filter(v => !!v) : field.value;

        return (
          <InputDropdown
            {...inputProps}
            {...field}
            dropdownMinWidth={dropdownMinWidth}
            error={fieldState.error?.message}
            hasFullHeight={hasFullHeight}
            helperText={helperText}
            label={label}
            menuProps={menuProps}
            multiple={multiple}
            options={options}
            placeholder={placeholder}
            size={size}
            value={value}
            onValueUpdate={onValueUpdate}
          />
        );
      }}
      rules={rulesProp}
    />
  );
};

export default FormInputDropdown;
